import { z } from "zod";
const id = z.uuid(),
  time = z.iso.datetime();
const base = {
  id,
  createdAt: time,
  updatedAt: time,
  revision: z.number().int().positive(),
  source: z.literal("user"),
  archivedAt: time.nullable(),
  deletedAt: time.nullable(),
  attachmentIds: z.array(id).max(30),
};
export const sopStepSchema = z.strictObject({
  id,
  group: z.string().max(200),
  title: z.string().trim().min(1).max(200),
  body: z.string().max(20000),
  required: z.boolean(),
});
const fields = {
  title: z.string().trim().min(1).max(200),
  purpose: z.string().max(20000),
  preparation: z.string().max(20000),
  steps: z.array(sopStepSchema).min(1).max(200),
};
export const sopFieldsSchema = z
  .strictObject(fields)
  .refine(
    (v) => new Set(v.steps.map((s) => s.id)).size === v.steps.length,
    "步骤标识重复",
  );
export type SopFields = z.infer<typeof sopFieldsSchema>;
export const sopTemplateSchema = z.strictObject({
  ...base,
  currentVersionId: id,
  pinned: z.boolean(),
});
export const sopVersionSchema = z
  .strictObject({
    id,
    templateId: id,
    version: z.number().int().positive(),
    createdAt: time,
    ...fields,
    attachmentIds: z.array(id).max(30),
  })
  .refine(
    (v) => new Set(v.steps.map((s) => s.id)).size === v.steps.length,
    "步骤标识重复",
  );
export const sopResultSchema = z
  .strictObject({
    stepId: id,
    status: z.enum(["pending", "completed", "skipped", "not-applicable"]),
    note: z.string().max(20000),
    reason: z.string().max(20000),
  })
  .refine(
    (v) =>
      !["skipped", "not-applicable"].includes(v.status) || !!v.reason.trim(),
    "跳过或不适用须记录原因",
  );
export const sopRunSchema = z
  .strictObject({
    ...base,
    templateId: id,
    templateVersionId: id,
    state: z.enum(["active", "paused", "completed", "partial", "cancelled"]),
    cursor: z.number().int().nonnegative(),
    results: z.array(sopResultSchema).max(200),
    outcomeReason: z.string().max(20000),
    endedAt: time.nullable(),
  })
  .refine(
    (v) =>
      ["active", "paused"].includes(v.state)
        ? v.endedAt === null
        : v.endedAt !== null,
    "执行结束时间与状态不一致",
  )
  .refine(
    (v) =>
      !["partial", "cancelled"].includes(v.state) || !!v.outcomeReason.trim(),
    "部分完成或取消须记录原因",
  );
export const sopDraftSchema = z.strictObject({
  id,
  targetId: id,
  baseRevision: z.number().int().nonnegative(),
  revision: z.number().int().positive(),
  createdAt: time,
  updatedAt: time,
  deletedAt: time.nullable(),
  payload: z.strictObject({
    ...fields,
    title: z.string().max(200),
    steps: z
      .array(sopStepSchema.extend({ title: z.string().max(200) }))
      .max(200),
  }),
});
export const sopRevisionSchema = z.strictObject({
  id: z.string().max(100),
  entityType: z.enum(["sop-template", "sop-run"]),
  entityId: id,
  revision: z.number().int().positive(),
  reason: z.enum([
    "created",
    "edited",
    "paused",
    "resumed",
    "completed",
    "partial",
    "cancelled",
    "cursor",
    "trashed",
    "restored",
    "history-restored",
  ]),
  recordedAt: time,
  snapshot: z.union([sopTemplateSchema, sopRunSchema]),
});
export type SopTemplate = z.infer<typeof sopTemplateSchema>;
export type SopVersion = z.infer<typeof sopVersionSchema>;
export type SopRun = z.infer<typeof sopRunSchema>;
export type SopDraft = z.infer<typeof sopDraftSchema>;
export type SopRevision = z.infer<typeof sopRevisionSchema>;
export type SopResult = z.infer<typeof sopResultSchema>;
export function assertRunBinding(run: SopRun, version: SopVersion) {
  if (
    run.templateId !== version.templateId ||
    run.templateVersionId !== version.id ||
    run.results.length !== version.steps.length ||
    run.cursor >= version.steps.length ||
    new Set(run.results.map((r) => r.stepId)).size !== version.steps.length ||
    version.steps.some((s, i) => run.results[i].stepId !== s.id)
  )
    throw Error("执行与不可变模板版本不一致");
  if (
    run.state === "completed" &&
    version.steps.some(
      (s, i) => s.required && run.results[i].status !== "completed",
    )
  )
    throw Error("必做步骤未完成，不能记录全部完成");
}
export const blankSop = (): SopFields => ({
  title: "",
  purpose: "",
  preparation: "",
  steps: [
    { id: crypto.randomUUID(), group: "", title: "", body: "", required: true },
  ],
});
export const sopPath = (kind: "sop-template" | "sop-run", id: string) =>
  `/sop/${kind === "sop-template" ? "templates" : "runs"}/${id}`;
export const sopStateLabels = {
  active: "进行中",
  paused: "已暂停",
  completed: "全部完成",
  partial: "部分完成",
  cancelled: "已取消",
};
export const sopResultLabels = {
  pending: "未完成",
  completed: "完成",
  skipped: "跳过",
  "not-applicable": "不适用",
};
